import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatString } from "../../../../../utils/constants/stringUtils";
import { convertToUserTimezone } from "../../../../../utils/dateService";
import { snakeCaseToTitleLabel } from "../../../../../utils/UserUtils";

// Shared, pure helpers for monthly challenge cards (ReBet, Referral, Casino).
// Keeping tier arithmetic + reward formatting in one place means every card
// agrees on the numbers instead of each data module reimplementing them.

// Formats a milestone reward, e.g. formatRewardLabel(5, "Rebet Cash") -> "5 Rebet
// Cash". The currency label is passed in (resolved from platform config via
// useRewardCurrencyLabel) rather than hardcoded, so it follows the operator's
// configured currency.
export const formatRewardLabel = (amount, currencyLabel) =>
  `${amount} ${currencyLabel}`;

// Bonus reward category / grant identifier → AppStrings label key, for the two
// bonuses whose display name differs from a plain title-casing of the wire value
// (Free Cash Pick ships as `free_pick`; both keep the "Free Cash Pick" copy).
// Everything else (e.g. `profit_boost`, `deposit_match`) title-cases cleanly, so
// it falls through to snakeCaseToTitleLabel rather than needing an entry here.
const BONUS_REWARD_LABEL_KEYS = {
  free_pick: "REWARD_TYPE_FREE_CASH_PICK",
  free_cash_pick: "REWARD_TYPE_FREE_CASH_PICK",
  profit_boost: "REWARD_TYPE_PROFIT_BOOST",
};

/**
 * Resolve the currency/reward label for a single tier reward (FE-2770).
 *
 * The BE ships each tier reward as `{ amount, currency, reward_type? }`, where
 * `currency` is `value`/`play` for the two platform currencies or a bonus
 * category (`free_pick`, `profit_boost`, `deposit_match`, …) and `reward_type`
 * is the bonus's grant id. PAM previously labelled every reward with the
 * primary currency name, so bonus tiers read "10 Rebet Cash" instead of the
 * reward the player actually receives. Mirror the challenge reward picker:
 *   - `value` → the operator's primary currency name (Rebet Cash),
 *   - `play`  → the operator's secondary currency name (Rebet Coins),
 *   - bonus   → the bonus's name (mapped label, else a title-cased fallback).
 */
export const resolveTierRewardCurrencyLabel = (
  reward,
  { primaryCurrencyLabel, secondaryCurrencyLabel } = {},
) => {
  const currency = typeof reward?.currency === "string" ? reward.currency : "";
  const rewardType =
    typeof reward?.reward_type === "string" ? reward.reward_type : "";

  if (currency === "value") {
    return primaryCurrencyLabel ?? AppStrings.PRIMARY_CURRENCY;
  }
  if (currency === "play") {
    return secondaryCurrencyLabel ?? AppStrings.SECONDARY_CURRENCY;
  }

  const labelKey =
    BONUS_REWARD_LABEL_KEYS[currency] || BONUS_REWARD_LABEL_KEYS[rewardType];
  if (labelKey && AppStrings[labelKey]) {
    return AppStrings[labelKey];
  }

  const fallbackSource = currency || rewardType;
  if (fallbackSource) {
    return snakeCaseToTitleLabel(fallbackSource);
  }

  // Legacy tiers ship no currency at all — keep the primary-currency default.
  return primaryCurrencyLabel ?? AppStrings.PRIMARY_CURRENCY;
};

// Full milestone label for a tier reward, e.g. "5 Rebet Cash" / "10 Free Cash
// Pick" / "20 Deposit Match". Amount comes from the reward, the currency label
// from the reward's currency/reward_type via resolveTierRewardCurrencyLabel.
export const formatTierRewardLabel = (reward, currencyLabels = {}) =>
  formatRewardLabel(
    Number(reward?.amount) || 0,
    resolveTierRewardCurrencyLabel(reward, currencyLabels),
  );

// FE-2435: Pick History "Result" values are amounts of the primary currency, so
// the design appends its shortcode (e.g. "42 RCH"). Only finite numeric values
// get the suffix — legacy payloads put a boolean won-flag in the same field and
// "true RCH" would be nonsense; those (and anything else non-numeric) render
// unchanged.
export const formatHistoryResult = (value, currencyShortcode) => {
  if (value == null || value === "" || typeof value === "boolean") {
    return value;
  }
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    return value;
  }
  return `${value} ${currencyShortcode}`;
};

const toFiniteNumber = (value) => {
  if (value == null || value === "" || typeof value === "boolean") {
    return null;
  }
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/** Sportsbook Result amount: `payout` (total return) when present, else
 *  `wager_amount + win_amount`. Null when neither can be resolved. */
export const getSportsbookPayoutAmount = (bet) => {
  const payout = toFiniteNumber(bet?.payout);
  if (payout != null) {
    return payout;
  }
  const wager = toFiniteNumber(
    bet?.wager_amount ?? bet?.amount ?? bet?.wager,
  );
  const winAmount = toFiniteNumber(bet?.win_amount);
  if (wager == null || winAmount == null) {
    return null;
  }
  return wager + winAmount;
};

/**
 * Client-aligned card header readout (FE-2557): the game-pass widget shows
 * progress toward the NEXT tier reward — "5/6 to next reward · Tier 1 of 3" —
 * while PAM used to show count over the FINAL threshold ("5/10"), so CS agents
 * comparing the two screens thought the numbers disagreed.
 *
 * The denominator is the first unreached tier's threshold; the tier indicator
 * is the 1-based tier the player is working toward (capped at the ladder size
 * once complete, when the numerator also caps at that final threshold so a
 * manually-passed task reads "4/4", not "1/4"). Returns `null` when the tier
 * ladder is empty or malformed so callers can fall back to the plain
 * count/goal counter (non-tiered tasks).
 *
 * @param {number} reachedCount how many tiers are fully reached (0-based);
 *   the "Tier N of M" label is 1-based (`reachedCount + 1`, capped at total).
 */
export const getNextRewardSummary = (count, thresholds, reachedCount) => {
  const total = Array.isArray(thresholds) ? thresholds.length : 0;
  if (
    total === 0 ||
    thresholds.some(
      (threshold) =>
        !Number.isFinite(Number(threshold)) || Number(threshold) <= 0,
    )
  ) {
    return null;
  }
  const numericReached = Number(reachedCount);
  const safeReached = Number.isFinite(numericReached)
    ? Math.max(0, Math.min(numericReached, total))
    : 0;
  const completed = safeReached >= total;
  const nextThreshold = Number(
    thresholds[completed ? total - 1 : safeReached],
  );
  const safeCount = Number.isFinite(Number(count)) ? Number(count) : 0;
  const displayCount = completed
    ? nextThreshold
    : Math.min(safeCount, nextThreshold);
  return {
    counterText: formatString(AppStrings.GAME_PASS_MONTHLY_TO_NEXT_REWARD, {
      count: String(displayCount),
      threshold: String(nextThreshold),
    }),
    tierText: formatString(AppStrings.GAME_PASS_MONTHLY_TIER_OF, {
      current: String(Math.min(safeReached + 1, total)),
      total: String(total),
    }),
  };
};

/**
 * 1-based tier numbers whose thresholds sit strictly after `fromCount` and
 * at or before `toCount`. Empty when the bump does not cross a new node, or
 * when it would be a decrease (FE-2590 is forward-only).
 */
export const getGrantedTierNumbers = (fromCount, toCount, thresholds) => {
  if (!Array.isArray(thresholds) || toCount <= fromCount) {
    return [];
  }
  return thresholds.flatMap((threshold, index) => {
    const numeric = Number(threshold);
    if (!Number.isFinite(numeric) || numeric <= fromCount || numeric > toCount) {
      return [];
    }
    return [index + 1];
  });
};

export const formatGrantedTiersLabel = (tierNumbers) => {
  if (!Array.isArray(tierNumbers) || tierNumbers.length === 0) {
    return null;
  }
  if (tierNumbers.length === 1) {
    return formatString(AppStrings.GAME_PASS_MONTHLY_GRANTS_TIER, {
      tier: String(tierNumbers[0]),
    });
  }
  return formatString(AppStrings.GAME_PASS_MONTHLY_GRANTS_TIERS, {
    from: String(tierNumbers[0]),
    to: String(tierNumbers[tierNumbers.length - 1]),
  });
};

/** Non-empty `tier_claims` only — `[]` is not per-tier data, so the task flag still applies. */
export const hasMonthlyTierClaims = (tierClaims) =>
  Array.isArray(tierClaims) && tierClaims.length > 0;

/**
 * Per-tier `tier_claims` rows are authoritative when present (FE-2625).
 * Task-level `reward_claimed` means "at least one reward claimed", so it must
 * not paint every reached node Claimed. Without per-tier rows, that flag still
 * covers reached nodes (FE-2556 fallback).
 */
export const isMonthlyRewardClaimed = ({
  claimState,
  taskRewardClaimed,
  index,
  reachedCount,
  hasTierClaims,
}) => {
  if (claimState?.claimed === true || claimState?.reward_claimed === true) {
    return true;
  }
  if (hasTierClaims) {
    return false;
  }
  return taskRewardClaimed === true && index < reachedCount;
};

// Derives the "{reached}/{total} challenges" + percentage shown next to a tier
// bar. `reachedCount` is clamped so callers can pass an unbounded server value.
export const getTierProgressSummary = (reachedCount, totalMilestones) => {
  const safeReached = Math.max(0, Math.min(reachedCount, totalMilestones));
  const percent =
    totalMilestones > 0 ? Math.round((safeReached / totalMilestones) * 100) : 0;
  return {
    challengesText: `${safeReached}/${totalMilestones} ${AppStrings.USER_GAME_PASS_MONTHLY_CHALLENGES_SUFFIX}`,
    percentLabel: `${percent}%`,
  };
};

// Same display format as User Manager bet tables (BetTable, CasinoBetsTable,
// Poker HandTable, PikkemBets): admin timezone via dateService.
export const HISTORY_TIMESTAMP_DISPLAY_FORMAT = "DD MMMM YYYY, HH:mm:ss";

/**
 * Formats history timestamps for the Monthly Challenges support tables.
 * Accepts unix seconds (int/float), unix milliseconds, or ISO date strings and
 * returns the PAM admin-timezone display used across User Manager tables,
 * e.g. `04 May 2026, 12:01:19`.
 */
export const formatHistoryTimestamp = (value) => {
  if (value == null || value === "") {
    return "";
  }
  if (typeof value === "string" && !value.trim()) {
    return "";
  }

  const formatted = convertToUserTimezone(
    value,
    HISTORY_TIMESTAMP_DISPLAY_FORMAT,
  );
  // dateService uses "-" for nullish empties; keep history cells blank instead.
  return formatted === "-" ? "" : formatted;
};
